import { ForwardedRef, forwardRef, ReactNode } from "react";

import ContentSettingsGroupsProvider from "@/admin/ContentSettingsGroupsContext";
import DynamicBreadcrumbs from "@/components/DynamicBreadcrumbs";
import HideWhenUnlockOrPreviewContentBannerIsShown from "@/components/UnlockOrPreviewContentBanner";
import AdminConsoleSidebar from "../AdminConsoleSidebar";
import useAdminConsoleBreadcrumbsLinks from "../hooks/useAdminConsoleBreadcrumbsLinks";

import {
  headerContainerCss,
  breadcrumbsCss,
  containerCss,
  containerPaddingTopModifierCss,
  contentContainerCss,
  mainContainerCss,
  titleCss,
  separatorCss,
  descriptionPageCss,
  headerCss,
  statusContainerCss,
} from "./AdminConsoleBasePage.style";

interface AdminConsoleBasePageProps {
  /**
   * The sub menu item that will be added to the sidebar when the user is in a record page.
   */
  subMenuRecordInfo?: {
    ariaLabel: string;
    id: string;
    content: ReactNode;
    title: string;
  };

  /**
   * The title of the page.
   */
  title?: string;

  /**
   * Optional node or array of nodes that will render in the body section.
   */
  children?: ReactNode;

  /**
   * Optional boolean indicating whether or not to display the separator line between the header and the content.
   */
  showHeaderSeparator?: boolean;

  /**
   * Optional boolean that indicates if the header is not to be displayed.
   */
  hiddenHeader?: boolean;

  /**
   * Optional node with the different  actions buttons.
   */
  actions?: ReactNode;

  /**
   * The sub menu item that will be added to the breadcrumb when the user is in a main section.
   */
  breadcrumbsSection?: {
    href: string | null;
    contents: string;
  };

  /**
   * The optional sub section that will be added to the breadcrumb when the user is in a a record page.
   */
  breadcrumbsSubsection?: {
    href: string | null;
    contents: string;
  };

  /**
   * The optional page description that goes below the page title.
   */
  pageDescription?: string | ReactNode;

  /**
   * Optional Badge Status Component
   */
  BadgeStatusComponent?: ReactNode;

  /**
   * Optional pageName
   * Defaults to empty string
   * Needs to be provided for `content settings` and `goals`
   * pages to ensure lite users are not able to take actions on
   * those pages since they currently show lite unlock banners.
   */
  pageName?: string;
}

/**
 * AdminConsoleBasePage is the template used for each admin console page.  You can
 * pass the components as children and they will accommodate in the body section
 * with correct spacing applied.
 */
const AdminConsoleBasePage = forwardRef(
  (props: AdminConsoleBasePageProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      actions,
      title,
      children,
      showHeaderSeparator,
      hiddenHeader,
      subMenuRecordInfo,
      breadcrumbsSection,
      breadcrumbsSubsection,
      pageDescription,
      BadgeStatusComponent,
      pageName = "",
    } = props;

    const breadcrumbsLinks = useAdminConsoleBreadcrumbsLinks(
      breadcrumbsSection,
      breadcrumbsSubsection,
    );

    return (
      <div css={mainContainerCss} ref={ref}>
        <ContentSettingsGroupsProvider>
          <AdminConsoleSidebar subMenuRecordInfo={subMenuRecordInfo} />
        </ContentSettingsGroupsProvider>
        <div css={contentContainerCss}>
          {!hiddenHeader && (
            <div css={headerContainerCss}>
              <DynamicBreadcrumbs
                breadcrumbsCss={breadcrumbsCss}
                breadcrumbsLinks={breadcrumbsLinks}
              />
              {BadgeStatusComponent && (
                <div css={statusContainerCss}>{BadgeStatusComponent}</div>
              )}
              <div css={headerCss}>
                {title && <h1 css={titleCss}>{title}</h1>}
                <HideWhenUnlockOrPreviewContentBannerIsShown
                  pageName={pageName}
                >
                  {!pageDescription ? actions : null}
                </HideWhenUnlockOrPreviewContentBannerIsShown>
              </div>
              {pageDescription && (
                <div data-testid="page-description" css={descriptionPageCss}>
                  {pageDescription}
                  <HideWhenUnlockOrPreviewContentBannerIsShown
                    pageName={pageName}
                  >
                    {actions}
                  </HideWhenUnlockOrPreviewContentBannerIsShown>
                </div>
              )}
            </div>
          )}
          {showHeaderSeparator && (
            <span data-testid="separator" css={separatorCss} />
          )}
          <div
            css={
              showHeaderSeparator
                ? containerCss
                : [containerCss, containerPaddingTopModifierCss]
            }
          >
            {children}
          </div>
        </div>
      </div>
    );
  },
);

export default AdminConsoleBasePage;
